/* MM working checklists: private progress and verified exact collection matches. */
(function (host) {
  'use strict';
  const STORAGE_KEY = 'mm_collector_checklist_progress_v1';
  const ownedStatuses = new Set(['owned', 'for_sale', 'for_trade']);
  const clean = value => typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
  const object = value => !!value && typeof value === 'object' && !Array.isArray(value);
  const fields = ['collector_number', 'toy_number', 'casting_name', 'variation_name', 'series'];

  function canonicalVariationPath(value) {
    try {
      const url = new URL(clean(value), 'https://munclemikes.com');
      if (!clean(value) || url.protocol !== 'https:' || url.hostname !== 'munclemikes.com' || url.username || url.password) return '';
      return /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?pages\/hot-wheels-variation-[a-z0-9][a-z0-9-]*$/.test(url.pathname) ? url.pathname.replace(/^\/[a-z]{2}(?:-[a-z]{2})?\/pages\//, '/pages/') : '';
    } catch (_) { return ''; }
  }

  function exactIdentity(item) {
    if (!object(item)) return '';
    const verified = item.verification_status === 'verified' || item.verified === true;
    if (!verified) return '';
    const type = clean(item.entity_type || item.catalog_type);
    if (type && !['variation', 'release'].includes(type)) return '';
    const catalogId = clean(item.catalog_id || item.catalogId);
    if (catalogId && ['variation', 'release'].includes(type)) return catalogId;
    const variationId = clean(item.variation_id);
    if (variationId && type !== 'release') return 'variation:' + variationId;
    const releaseId = clean(item.release_id);
    if (releaseId && type === 'release') return 'release:' + releaseId;
    // The current read-only checklist API supplies an exact variation route and UUID,
    // rather than variation_id. A generic verified row ID is insufficient.
    const route = canonicalVariationPath(item.canonical_path);
    if (!type && /^[a-z0-9][a-z0-9-]*$/.test(clean(item.slug)) &&
      route === '/pages/hot-wheels-variation-' + clean(item.slug) &&
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(clean(item.id)) &&
      clean(item.casting_name) && clean(item.variation_name)) return 'variation:' + clean(item.id);
    return '';
  }

  function rowKey(item, scope) {
    const identity = exactIdentity(item);
    return JSON.stringify([clean(scope.year), clean(scope.series), identity || '',
      ...fields.map(field => clean(item[field])), clean(item.release_year)]);
  }

  function ownership(item, records) {
    const identity = exactIdentity(item);
    if (!identity) return {identity: '', ownedCopies: 0, wanted: false};
    const route = canonicalVariationPath(item.canonical_path);
    const matches = records.filter(record => record.catalogId === identity ||
      (route && !/^(?:variation|release):/.test(clean(record.catalogId)) && canonicalVariationPath(record.url) === route));
    return {
      identity,
      ownedCopies: matches.filter(record => ownedStatuses.has(record.status)).reduce((sum, record) => sum + Math.max(1, Math.trunc(Number(record.quantity) || 1)), 0),
      wanted: matches.some(record => record.status === 'want')
    };
  }

  function personalRecordId(item, scope, status) {
    // A private entry ID is a stable client record key, never a catalog identity.
    const value = rowKey(item, scope);
    let first = 2166136261, second = 5381;
    for (let i = 0; i < value.length; i++) {
      first = Math.imul(first ^ value.charCodeAt(i), 16777619);
      second = Math.imul(second, 33) ^ value.charCodeAt(i);
    }
    return 'checklist-personal-' + status + '-' + (first >>> 0).toString(16) + '-' + (second >>> 0).toString(16);
  }

  function personalMatches(item, scope, records) {
    return records.filter(record => ['owned', 'want'].some(status => record.recordId === personalRecordId(item, scope, status)));
  }

  function addRowRecord(item, scope, status, engine) {
    if (!engine || !['owned', 'want'].includes(status)) throw new Error('Collection saving is unavailable.');
    const saved = engine.getRecords(), match = ownership(item, saved);
    if (match.identity && (status === 'owned' ? match.ownedCopies > 0 : match.wanted)) return {added: false, identity: match.identity};
    const personalId = personalRecordId(item, scope, status);
    // Preserve any user edits to a previous personal entry, even if its status changed.
    if (!match.identity && (saved.some(record => record.recordId === personalId) ||
      personalMatches(item, scope, saved).some(record => status === 'owned' ? ownedStatuses.has(record.status) : record.status === 'want'))) return {added: false, identity: ''};
    const record = {
      title: [item.release_year || scope.year, item.casting_name, item.variation_name].map(clean).filter(Boolean).join(' ') || 'Unidentified collector item',
      manufacturer: clean(item.manufacturer || item.manufacturer_name), casting: clean(item.casting_name),
      releaseYear: clean(item.release_year || scope.year), scale: clean(item.scale),
      url: clean(item.canonical_path), imageUrl: clean(item.primary_image_url),
      status, quantity: 1, purchasePrice: null,
      notes: match.identity ? 'Added from an MM checklist.' :
        'Personal checklist entry. Compare your item with the release details before choosing a catalog match.'
    };
    if (match.identity) record.catalogId = match.identity;
    else record.recordId = personalId;
    engine.upsert(record);
    return {added: true, identity: match.identity};
  }

  function summarize(items, scope, progress, records) {
    const exactIds = new Set();
    const ownedIds = new Set();
    let markedRows = 0;
    for (const item of items) {
      const match = ownership(item, records);
      if (match.identity) exactIds.add(match.identity);
      if (match.ownedCopies) ownedIds.add(match.identity);
      const key = rowKey(item, scope);
      if (Object.prototype.hasOwnProperty.call(progress, key) ? progress[key] === true : match.ownedCopies > 0) markedRows++;
    }
    return {returnedRows: items.length, markedRows, unmarkedRows: items.length - markedRows,
      exactIdentities: exactIds.size, ownedIdentities: ownedIds.size,
      verifiedGaps: exactIds.size - ownedIds.size};
  }

  function toggle(item, scope, checked, progress, engine) {
    const next = {...progress, [rowKey(item, scope)]: !!checked};
    const match = ownership(item, engine ? engine.getRecords() : []);
    let added = false;
    // A checkbox never deletes, edits, or multiplies existing physical copies.
    if (checked && match.identity && !match.ownedCopies && engine) {
      added = addRowRecord(item, scope, 'owned', engine).added;
    }
    return {progress: next, added, identity: match.identity};
  }

  const api = {STORAGE_KEY, exactIdentity, canonicalVariationPath, rowKey, ownership, summarize, toggle, personalRecordId, personalMatches, addRowRecord};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!host || !host.document) return;
  if (host.MMCollectorChecklistBridge) return;
  host.MMCollectorChecklistBridge = api;
  const document = host.document;
  const instances = new WeakMap();
  const activeInstances = new Set();
  let progress = {};
  let storageAvailable = true;
  try {
    const parsed = JSON.parse(host.localStorage.getItem(STORAGE_KEY) || '{}');
    if (object(parsed)) for (const [key, value] of Object.entries(parsed)) if (typeof value === 'boolean') progress[key] = value;
  } catch (_) { storageAvailable = false; }
  function saveProgress(next) {
    try { host.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); storageAvailable = true; }
    catch (_) { storageAvailable = false; throw new Error('Checklist progress could not be saved.'); }
    progress = next;
  }
  function rowsFromTable(table) {
    return [...table.querySelectorAll('tbody tr')].map(row => {
      const cells = [...row.querySelectorAll('td')];
      return Object.fromEntries(fields.map((field, index) => {
        const value = clean(cells[index + 1] && cells[index + 1].textContent);
        return [field, value === '—' ? '' : value];
      }));
    });
  }
  function ownerMatches(engine, provider) {
    const state = engine && engine.getState ? engine.getState() : {};
    const membership = provider && provider.getState ? provider.getState() : {};
    return !state.cloudMeta || !state.cloudMeta.scopeHash || state.cloudMeta.scopeHash === membership.ownerScopeHash;
  }
  function records() { return host.MMCollectorEngine && ownerMatches(host.MMCollectorEngine, host.MMCollectorMembership) ? host.MMCollectorEngine.getRecords() : []; }
  function update(instance) {
    const saved = records();
    instance.rows.forEach((row, index) => {
      const item = instance.items[index];
      const box = row.querySelector('input[type="checkbox"]');
      if (!box || !item) return;
      const match = ownership(item, saved);
      const personal = personalMatches(item, instance.scope, saved);
      const key = rowKey(item, instance.scope);
      box.checked = Object.prototype.hasOwnProperty.call(progress, key) ? progress[key] === true : match.ownedCopies > 0;
      // Keep the original checklist's CSV snapshot aligned via its original listener.
      item.owned = box.checked;
      if (instance.snapshotItems && instance.snapshotItems[index]) instance.snapshotItems[index].owned = box.checked;
      let badge = row.querySelector('[data-mm-checklist-badge]');
      if (!badge) {
        badge = document.createElement('span'); badge.dataset.mmChecklistBadge = '';
        badge.style.cssText = 'display:block;font-size:.8em;color:#075282;margin-top:4px';
        box.parentNode.append(badge);
      }
      badge.textContent = match.ownedCopies ? 'Owned · ' + match.ownedCopies + (match.ownedCopies === 1 ? ' copy' : ' copies') + (match.wanted ? ' · Wanted' : '') :
        match.wanted ? 'Wanted' : match.identity ? 'Catalog match · not owned' :
          (personal.length ? 'Personal entry saved · release not identified' : 'Private checklist progress · release not identified');
      let actions = row.querySelector('[data-mm-checklist-row-actions]');
      if (!actions) {
        actions = document.createElement('div'); actions.dataset.mmChecklistRowActions = '';
        actions.style.cssText = 'display:flex;flex-wrap:wrap;gap:4px;margin-top:5px';
        ['owned', 'want'].forEach(status => {
          const button = document.createElement('button'); button.type = 'button'; button.dataset.mmChecklistSaveStatus = status;
          button.style.cssText = 'min-height:44px;background:#fff;color:#075282;border:1px solid #a5b9c7;border-radius:4px;font:inherit;font-size:.8em;padding:5px';
          actions.append(button);
        });
        box.parentNode.append(actions);
      }
      actions.querySelectorAll('[data-mm-checklist-save-status]').forEach(button => {
        const status = button.dataset.mmChecklistSaveStatus;
        const exists = match.identity ? (status === 'owned' ? match.ownedCopies > 0 : match.wanted) : personal.some(record =>
          record.recordId === personalRecordId(item, instance.scope, status) || (status === 'owned' ? ownedStatuses.has(record.status) : record.status === 'want'));
        button.disabled = exists || !host.MMCollectorEngine || !ownerMatches(host.MMCollectorEngine, host.MMCollectorMembership) || !!button.dataset.mmMemberPending;
        button.textContent = exists ? (match.identity ? (status === 'owned' ? 'Owned entry saved' : 'Wishlist entry saved') : 'Personal ' + (status === 'owned' ? 'owned' : 'wish') + ' entry saved') :
          (match.identity ? (status === 'owned' ? 'Add owned copy' : 'Add to wishlist') : 'Add personal ' + (status === 'owned' ? 'owned' : 'wish') + ' entry');
        button.setAttribute('aria-label', button.textContent + ': ' + (clean(item.casting_name) || 'Unidentified collectible'));
      });
    });
    const stats = summarize(instance.items, instance.scope, progress, saved);
    instance.summary.textContent = stats.returnedRows + ' returned rows · ' + stats.markedRows + ' marked · ' + stats.unmarkedRows + ' unmarked in this working list.' +
      (stats.exactIdentities ? ' ' + stats.ownedIdentities + ' of ' + stats.exactIdentities + ' verified identities owned; ' + stats.verifiedGaps + ' verified gaps in this returned list.' : '') +
      ' This list does not establish a complete release set. ' +
      (storageAvailable ? 'Checklist marks are saved on this device. ' : 'Checklist marks could not be saved on this device. ') +
      'Check a catalog match to add one copy to My Collection. Unchecking removes the checklist mark; manage saved copies in Collection Manager. Use a personal entry when you have not identified the exact release.';
  }
  function beginOperation(instance, row, control) {
    const index = instance.rows.indexOf(row);
    if (index < 0 || !instance.items[index]) return null;
    const provider = host.MMCollectorMembership;
    const membership = provider && provider.getState ? provider.getState() : null;
    const operation = {instance, row, control, index, generation: instance.generation,
      version: (instance.operations.get(row) || 0) + 1, table: instance.table,
      item: {...instance.items[index]}, sourceItem: instance.items[index], scope: {...instance.scope},
      selection: JSON.stringify([clean(instance.root.querySelector('[data-checklist-year]')?.value), clean(instance.root.querySelector('[data-checklist-series]')?.value)]),
      provider, engine: host.MMCollectorEngine, owner: membership && membership.ownerScopeHash};
    operation.key = rowKey(operation.item, operation.scope);
    instance.operations.set(row, operation.version);
    return operation;
  }
  function operationCurrent(operation) {
    const {instance, row} = operation;
    return instance.active && instance.generation === operation.generation && instance.table === operation.table &&
      instance.operations.get(row) === operation.version && instance.rows[operation.index] === row &&
      instance.items[operation.index] === operation.sourceItem && rowKey(operation.sourceItem, operation.scope) === operation.key &&
      clean(operation.sourceItem.canonical_path) === clean(operation.item.canonical_path) &&
      instance.root.querySelector('[data-checklist-table] table') === operation.table &&
      JSON.stringify([clean(instance.root.querySelector('[data-checklist-year]')?.value), clean(instance.root.querySelector('[data-checklist-series]')?.value)]) === operation.selection;
  }
  function assertAuthorizedOperation(operation) {
    if (!operationCurrent(operation) || host.MMCollectorEngine !== operation.engine || host.MMCollectorMembership !== operation.provider) throw new Error('The checklist or Collector account changed while access was checked. Review the current row and try again.');
    if (!ownerMatches(operation.engine, operation.provider)) throw new Error('Sign in to the original collection owner account before adding to its saved collection.');
    const membership = operation.provider && operation.provider.getState ? operation.provider.getState() : null;
    if (membership && (!membership.accountVerified || (operation.owner && membership.ownerScopeHash !== operation.owner))) throw new Error('The Collector account changed while this entry was pending. Review the row and check the account again.');
  }
  function attach(root, suppliedItems, scope) {
    const instance = instances.get(root) || mount(root);
    if (!instance) return;
    const table = root.querySelector('[data-checklist-table] table');
    if (!table) return;
    const domItems = rowsFromTable(table);
    let items = domItems;
    // Positional data is trusted only after all visible cells match the API snapshot.
    if (Array.isArray(suppliedItems) && suppliedItems.length === domItems.length && suppliedItems.every((item, index) =>
      object(item) && fields.every(field => clean(item[field]) === clean(domItems[index][field])))) {
      items = suppliedItems; instance.snapshotItems = suppliedItems;
    } else instance.snapshotItems = null;
    instance.generation++;
    instance.table = table;
    instance.items = items;
    instance.scope = scope || {year: clean(root.querySelector('[data-checklist-year]')?.value), series: clean(root.querySelector('[data-checklist-series]')?.value)};
    instance.rows = [...table.querySelectorAll('tbody tr')];
    update(instance);
  }
  function mount(root) {
    if (instances.has(root)) return instances.get(root);
    const container = root.querySelector('[data-checklist-table]');
    if (!container) return null;
    const summary = document.createElement('p');
    summary.dataset.mmChecklistSummary = ''; summary.setAttribute('role', 'status');
    summary.style.cssText = 'color:#33475b;line-height:1.5';
    container.insertAdjacentElement('afterend', summary);
    const listeners = new AbortController();
    const instance = {root, summary, table: null, items: [], rows: [], scope: {}, snapshotItems: null, listeners, generation: 0, operations: new WeakMap(), active: true};
    instances.set(root, instance);
    activeInstances.add(instance);
    container.addEventListener('click', async event => {
      const button = event.target.closest && event.target.closest('[data-mm-checklist-save-status]');
      if (!button || !container.contains(button) || button.dataset.mmMemberPending) return;
      const operation = beginOperation(instance, button.closest('tr'), button);
      if (!operation) return;
      const status = button.dataset.mmChecklistSaveStatus;
      const previousMark = Object.prototype.hasOwnProperty.call(progress, operation.key) ? progress[operation.key] === true : ownership(operation.item, records()).ownedCopies > 0;
      button.dataset.mmMemberPending = 'true'; button.disabled = true;
      let collectionSaved = false;
      try {
        if (!operation.provider || typeof operation.provider.requireVerified !== 'function') throw new Error('Sign in to your free Collector account to add collection entries.');
        await operation.provider.requireVerified();
        assertAuthorizedOperation(operation);
        if (button.dataset.mmChecklistSaveStatus !== status) throw new Error('The row action changed while membership was checked. Try again.');
        const result = addRowRecord(operation.item, operation.scope, status, operation.engine);
        collectionSaved = result.added;
        if (status === 'owned') saveProgress({...progress, [operation.key]: true});
        update(instance);
        summary.textContent = result.added ? (result.identity ? 'Collection entry saved. ' : 'Personal entry saved; the exact release still needs to be identified. ') + summary.textContent :
          'Your existing entry was preserved. ' + summary.textContent;
      } catch (error) {
        if (!operationCurrent(operation)) return;
        if (collectionSaved) progress = {...progress, [operation.key]: previousMark};
        update(instance);
        summary.textContent = collectionSaved ? 'Your collection entry was saved, but the checklist mark could not be saved and has been restored.' :
          (error.message || 'The collection entry could not be saved. Your existing collection records were preserved.');
      } finally { delete button.dataset.mmMemberPending; if (instance.active) { const message = summary.textContent; update(instance); summary.textContent = message; } }
    }, {signal: listeners.signal});
    container.addEventListener('change', async event => {
      const box = event.target;
      if (!box.matches || !box.matches('tbody input[type="checkbox"]')) return;
      const operation = beginOperation(instance, box.closest('tr'), box);
      if (!operation) return;
      const checked = !!box.checked;
      const previousMark = Object.prototype.hasOwnProperty.call(progress, operation.key) ? progress[operation.key] === true : ownership(operation.item, records()).ownedCopies > 0;
      try {
        const before = ownership(operation.item, records());
        if (checked && before.identity && !before.ownedCopies) {
          if (!operation.provider || typeof operation.provider.requireVerified !== 'function') throw new Error('Sign in to your free Collector account to add this copy.');
          await operation.provider.requireVerified();
          assertAuthorizedOperation(operation);
        }
        if (!operationCurrent(operation) || box.checked !== checked) return;
        const result = toggle(operation.item, operation.scope, checked, progress, operation.engine);
        saveProgress(result.progress);
        update(instance);
      } catch (error) {
        if (!operationCurrent(operation)) return;
        progress = {...progress, [operation.key]: previousMark};
        update(instance);
        summary.textContent = error.message || 'The checklist mark was not saved and has been restored. Any successfully saved collection copy remains in My Collection.';
      }
    }, {signal: listeners.signal});
    host.addEventListener('mm:collector-engine-changed', () => update(instance), {signal: listeners.signal});
    host.addEventListener('mm:membership-change', () => update(instance), {signal: listeners.signal});
    const observer = new MutationObserver(() => {
      const table = container.querySelector('table');
      if (table && table !== instance.table) attach(root);
      else if (!table) { instance.generation++; instance.table = null; instance.items = []; instance.rows = []; summary.textContent = ''; }
    });
    observer.observe(container, {childList: true, subtree: true});
    instance.observer = observer;
    if (container.querySelector('table')) attach(root);
    return instance;
  }
  api.attachRows = attach;
  document.addEventListener('mm:collector-checklist-rendered', event => {
    const detail = event.detail || {};
    if (detail.root && detail.root.matches('[data-mm-phase-five]')) attach(detail.root, detail.items, {year: detail.year, series: detail.series});
  });
  const roots = scope => [...scope.querySelectorAll('[data-mm-phase-five]'), ...(scope.matches && scope.matches('[data-mm-phase-five]') ? [scope] : [])];
  const start = scope => roots(scope).forEach(mount);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => start(document), {once: true});
  else start(document);
  document.addEventListener('shopify:section:load', event => start(event.target));
  document.addEventListener('shopify:section:unload', event => roots(event.target).forEach(root => {
    const instance = instances.get(root);
    if (instance) { instance.active = false; instance.generation++; instance.listeners.abort(); instance.observer.disconnect(); instance.summary.remove(); instances.delete(root); activeInstances.delete(instance); }
  }));
  host.addEventListener('storage', event => {
    if (event.key !== STORAGE_KEY) return;
    try {
      const parsed = JSON.parse(event.newValue || '{}');
      if (!object(parsed)) return;
      const next = Object.fromEntries(Object.entries(parsed).filter(([, value]) => typeof value === 'boolean'));
      activeInstances.forEach(instance => instance.rows.forEach((row, index) => {
        const key = rowKey(instance.items[index], instance.scope);
        if (Object.prototype.hasOwnProperty.call(progress, key) !== Object.prototype.hasOwnProperty.call(next, key) || progress[key] !== next[key]) instance.operations.set(row, (instance.operations.get(row) || 0) + 1);
      }));
      progress = next;
      activeInstances.forEach(update);
    } catch (_) { /* A malformed external storage change does not replace current marks. */ }
  });
})(typeof window === 'undefined' ? null : window);
